import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, TrendingUp, DollarSign, Building2, Users, Calendar,
  ArrowUpRight, ArrowDownRight, PieChart, LineChart, Activity, Home, AlertTriangle
} from 'lucide-react';
import {
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart as RechartsBarChart, Bar,
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Treemap
} from 'recharts';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('12m');

  // Mock analytics data
  const revenueData = [
    { month: 'Jan', revenue: 78000, expenses: 12000, net: 66000 },
    { month: 'Feb', revenue: 78000, expenses: 15000, net: 63000 },
    { month: 'Mar', revenue: 78000, expenses: 18000, net: 60000 },
    { month: 'Apr', revenue: 78000, expenses: 10000, net: 68000 },
    { month: 'May', revenue: 78000, expenses: 14000, net: 64000 },
    { month: 'Jun', revenue: 78000, expenses: 16000, net: 62000 },
    { month: 'Jul', revenue: 78000, expenses: 13000, net: 65000 },
    { month: 'Aug', revenue: 78000, expenses: 17000, net: 61000 },
    { month: 'Sep', revenue: 78000, expenses: 11000, net: 67000 },
    { month: 'Oct', revenue: 78000, expenses: 15000, net: 63000 },
    { month: 'Nov', revenue: 78000, expenses: 14000, net: 64000 },
    { month: 'Dec', revenue: 78000, expenses: 12000, net: 66000 },
  ];

  const occupancyData = [
    { name: 'Occupied', value: 75, color: '#10B981' },
    { name: 'Vacant', value: 15, color: '#6B7280' },
    { name: 'Maintenance', value: 10, color: '#F59E0B' },
  ];

  const propertyTypeData = [
    { type: 'Store', count: 2, revenue: 27000 },
    { type: 'Warehouse', count: 2, revenue: 45000 },
    { type: 'Building', count: 1, revenue: 18000 },
  ];

  // Property usage distribution (from prototypes)
  const propertyUsageData = [
    { name: 'Commercial', value: 3, color: '#4338CA' },
    { name: 'Residential', value: 0, color: '#3B82F6' },
    { name: 'Undeveloped', value: 0, color: '#60A5FA' },
    { name: 'Industrial', value: 2, color: '#8B5CF6' },
  ];

  // Occupancy rate per property
  const propertyOccupancyData = [
    { name: 'Riyadh Main Store', occupancyRate: 100 },
    { name: 'Jeddah Warehouse', occupancyRate: 100 },
    { name: 'Dammam Office', occupancyRate: 100 },
    { name: 'Riyadh Retail', occupancyRate: 0 },
    { name: 'Khobar Storage', occupancyRate: 75 },
  ];

  // Tree map data for property portfolio
  const treeMapData = [
    { name: 'Commercial', size: 3 * 100, color: '#4338CA' },
    { name: 'Industrial', size: 2 * 100, color: '#8B5CF6' },
  ];

  const COLORS = ['#4338CA', '#3B82F6', '#60A5FA', '#8B5CF6'];

  const paymentStatusData = [
    { month: 'Jan', paid: 78000, pending: 43000, overdue: 0 },
    { month: 'Feb', paid: 78000, pending: 43000, overdue: 0 },
    { month: 'Mar', paid: 78000, pending: 43000, overdue: 0 },
  ];

  const stats = [
    {
      title: 'Total Revenue',
      value: 'SAR 936,000',
      change: '+5.2%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      title: 'Occupancy Rate',
      value: '75%',
      change: '+2.1%',
      trend: 'up',
      icon: Building2,
      color: 'text-blue-600'
    },
    {
      title: 'Active Tenants',
      value: '4',
      change: '+1',
      trend: 'up',
      icon: Users,
      color: 'text-purple-600'
    },
    {
      title: 'Average Rent',
      value: 'SAR 19,500',
      change: '+3.5%',
      trend: 'up',
      icon: TrendingUp,
      color: 'text-amber-600'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Analytics Dashboard</h2>
            <p className="text-sm text-gray-600 mt-1">
              Real estate performance metrics and insights
            </p>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="3m">Last 3 Months</option>
            <option value="6m">Last 6 Months</option>
            <option value="12m">Last 12 Months</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg bg-${stat.color.replace('text-', '')}/100`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {stat.change}
                </div>
              </div>
              <h3 className="text-sm text-gray-600 mb-1">{stat.title}</h3>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Revenue Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue & Expenses</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Legend />
            <Bar dataKey="revenue" fill="#F59E0B" name="Revenue" />
            <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
            <Line type="monotone" dataKey="net" stroke="#10B981" strokeWidth={2} name="Net Income" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Occupancy Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Occupancy Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={occupancyData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {occupancyData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>

        {/* Property Type Revenue */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue by Property Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsBarChart data={propertyTypeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="type" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Bar dataKey="revenue" fill="#F59E0B" name="Revenue (SAR)" />
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Charts from Prototypes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Usage Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Property Usage Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={propertyUsageData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {propertyUsageData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>

        {/* Occupancy Rate per Property */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Occupancy Rate per Property</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsBarChart data={propertyOccupancyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#6b7280" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Legend />
              <Bar dataKey="occupancyRate" fill="#10B981" name="Occupancy Rate (%)" />
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Property Portfolio Tree Map */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Property Portfolio Overview</h3>
        <ResponsiveContainer width="100%" height={300}>
          <Treemap
            data={treeMapData}
            dataKey="size"
            stroke="#fff"
            fill="#8884d8"
          >
            {treeMapData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Treemap>
        </ResponsiveContainer>
        <div className="mt-4 flex flex-wrap gap-4 justify-center">
          {treeMapData.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: entry.color }}></div>
              <span className="text-sm text-gray-700">{entry.name}: {Math.floor(entry.size / 100)} properties</span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Status Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <RechartsBarChart data={paymentStatusData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Legend />
            <Bar dataKey="paid" stackId="a" fill="#10B981" name="Paid" />
            <Bar dataKey="pending" stackId="a" fill="#F59E0B" name="Pending" />
            <Bar dataKey="overdue" stackId="a" fill="#EF4444" name="Overdue" />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Analytics;
